import type { FC, ReactNode } from 'react'

import { Quote } from 'lucide-react'
import { cn } from '@/lib/utils'
import SectionHeading from '@/components/landing/section-heading'
import { ACCENT_STYLES, TESTIMONIALS } from '@/constants/landing'

const initials = (name: string): string => {
    return name
        .split(' ')
        .map(part => part[0])
        .join('')
        .replace(/[^A-Za-zÀ-ÿ]/g, '')
        .slice(0, 2)
        .toUpperCase()
}

const PlayersSection: FC = (): ReactNode => {
    return (
        <section id='players' aria-labelledby='players-title' className='relative scroll-mt-20 py-24 sm:py-32'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <SectionHeading
                    id='players-title'
                    eyebrow='From the beta'
                    accent='pink'
                    align='center'
                    title='Built for night owls. Loved by them too.'
                    description="A few words from players who have been testing Neon Gambit since the first closed games."
                />

                <ul className='mt-14 grid gap-5 lg:grid-cols-3'>
                    {TESTIMONIALS.map(testimonial => {
                        const accent = ACCENT_STYLES[testimonial.accent]

                        return (
                            <li key={testimonial.handle}>
                                <figure className='flex h-full flex-col rounded-3xl border border-white/10 bg-card/60 p-7'>
                                    <Quote className={cn('size-7', accent.text)} style={{ filter: `drop-shadow(0 0 8px ${accent.hex})` }} />

                                    <blockquote className='mt-5 flex-1 text-base leading-relaxed text-foreground/90'>
                                        <p>{testimonial.quote}</p>
                                    </blockquote>

                                    <figcaption className='mt-7 flex items-center gap-3 border-t border-white/5 pt-6'>
                                        <span
                                            aria-hidden='true'
                                            className={cn('flex size-11 shrink-0 items-center justify-center rounded-full border font-heading text-sm font-semibold', accent.border, accent.bg, accent.text)}
                                            style={{ boxShadow: `0 0 18px ${accent.hex}40` }}
                                        >
                                            {initials(testimonial.name)}
                                        </span>

                                        <span className='min-w-0'>
                                            <span className='block text-sm font-semibold'>
                                                {testimonial.name}
                                            </span>

                                            <span className='block font-mono text-xs text-muted-foreground'>
                                                {testimonial.handle} · {testimonial.rating}
                                            </span>
                                        </span>
                                    </figcaption>
                                </figure>
                            </li>
                        )
                    })}
                </ul>
            </div>
        </section>
    )
}

export default PlayersSection
